import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import DOMPurify from 'dompurify'
import clsx from 'clsx'

import constants from 'constants/index'
import sanitizeStyle from './sanitizeStyle'
import useStyles from './styles'

// Lido pelo componente e pelo propTypes.
const { RICH_TEXT_SANITIZE_PROFILE } = constants.jodit

const IFRAME_SANITIZE_OPTIONS = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
}

// Mesmo perfil do backend (api_dponet: RichTexts::Sanitize), usado no parecer e no motivo da reprovação.
const RICH_TEXT_SANITIZE_OPTIONS = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'ul',
    'ol',
    'li',
    'span',
    'a',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'img',
    'table',
    'caption',
    'colgroup',
    'col',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
  ],
  ALLOWED_ATTR: [
    'href',
    'style',
    'src',
    'alt',
    'width',
    'height',
    'colspan',
    'rowspan',
  ],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
}

// O editor não tem upload: imagem só colada em base64. Imagem externa sai, como no backend.
const DATA_IMAGE_SOURCE_PATTERN = /^data:image\/(png|jpeg|gif|webp);base64,/i
const TABLE_SPAN_ATTRIBUTES = ['colspan', 'rowspan']
const TABLE_SPAN_PATTERN = /^([1-9]\d?|100)$/
const TABLE_CELL_TAGS = ['TD', 'TH']

const sanitizeRichText = (content) => {
  const body = DOMPurify.sanitize(content, {
    ...RICH_TEXT_SANITIZE_OPTIONS,
    RETURN_DOM: true,
  })

  body.querySelectorAll('img').forEach((image) => {
    if (!DATA_IMAGE_SOURCE_PATTERN.test(image.getAttribute('src') || '')) {
      image.remove()
    }
  })

  body.querySelectorAll('[style]').forEach((node) => {
    const style = sanitizeStyle(node.getAttribute('style'))
    if (style) node.setAttribute('style', style)
    else node.removeAttribute('style')
  })

  // colspan e rowspan só valem em célula de tabela, e só com número de 1 a 100.
  body.querySelectorAll('[colspan], [rowspan]').forEach((node) => {
    TABLE_SPAN_ATTRIBUTES.forEach((attribute) => {
      const value = node.getAttribute(attribute)
      if (value === null) return
      if (
        TABLE_CELL_TAGS.includes(node.tagName) &&
        TABLE_SPAN_PATTERN.test(value)
      )
        return

      node.removeAttribute(attribute)
    })
  })

  return body.innerHTML
}

const Preview = ({ content, allowIframe = false, sanitizeProfile }) => {
  const classes = useStyles()

  // O pai pode re-renderizar a cada tecla (watch do formulário): só re-sanitiza quando o conteúdo muda.
  const messageSanitizer = useMemo(
    () =>
      sanitizeProfile === RICH_TEXT_SANITIZE_PROFILE
        ? sanitizeRichText(content)
        : DOMPurify.sanitize(
            content,
            allowIframe ? IFRAME_SANITIZE_OPTIONS : {},
          ),
    [content, allowIframe, sanitizeProfile],
  )

  return (
    <div
      className={clsx('jodit-wysiwyg', classes.previewBox)}
      dangerouslySetInnerHTML={{ __html: messageSanitizer }}
    />
  )
}

Preview.propTypes = {
  content: PropTypes.string,
  allowIframe: PropTypes.bool,
  sanitizeProfile: PropTypes.oneOf([RICH_TEXT_SANITIZE_PROFILE]),
}

export default Preview
